import { useEffect, useState, useRef, useCallback } from "react";
import { Plus, Edit2, X, Trash2, Download, Building2, Upload, FileSpreadsheet, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

// Crear o ascender administrativos y ejecutivos es exclusivo del ejecutivo (ver Admins).
const ROLES = ["alumno", "profesor", "psicologo", "padre"];
const LEARNING_STYLES = ["visual", "auditivo", "lector", "kinestesico"];

const BASE_GRADES = [
    "1ro Primaria", "2do Primaria", "3ro Primaria", "4to Primaria",
    "5to Primaria", "6to Primaria", "1ro Secundaria", "2do Secundaria",
    "3ro Secundaria", "4to Secundaria", "5to Secundaria",
];

const GRADE_SECTIONS = BASE_GRADES.flatMap((g) =>
    (g === "5to Secundaria" ? ["A", "B", "C"] : ["A", "B"]).map((s) => `${g} ${s}`)
);

const SECTIONS = ["A", "B", "C"];
const PAGE_SIZE = 10;

export default function Usuarios() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [filterRole, setFilterRole] = useState("");
    const [filterBaseGrade, setFilterBaseGrade] = useState("");
    const [filterSection, setFilterSection] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [importing, setImporting] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/users", { params: filterRole ? { role: filterRole } : {} });
            setUsers(r.data || []);
            setPage(1);
        } catch (e) {
            toast.error("Error al cargar la lista de usuarios");
        }
    }, [filterRole]);

    useEffect(() => { load(); }, [load]);

    async function deactivate(id) {
        if (!window.confirm("¿Desactivar usuario?")) return;
        try {
            await api.delete("/users/" + id);
            toast.success("Usuario desactivado");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo desactivar el usuario");
        }
    }

    const availableSections = filterBaseGrade === "5to Secundaria" ? SECTIONS : ["A", "B"];

    const filteredUsers = users.filter((u) => {
        if (!u.active) return false;
        if (search && !u.name.toLowerCase().includes(search.toLowerCase()) &&
            !u.email.toLowerCase().includes(search.toLowerCase())) return false;
        if (filterBaseGrade && !(u.grade || "").startsWith(filterBaseGrade)) return false;
        if (filterSection && !(u.grade || "").endsWith(filterSection)) return false;
        return true;
    });

    const totalPages = Math.ceil(filteredUsers.length / PAGE_SIZE);
    const paginated = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div className="p-6 md:p-10 fade-in">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-semibold">Gestión de Usuarios</h1>
                    <p className="text-edu-body">Crea, edita y desactiva cuentas.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setImporting(true)} className="px-4 py-2.5 text-sm inline-flex items-center gap-2 rounded-xl border border-edu hover:bg-edu-soft">
                        <FileSpreadsheet size={14} /> Importar CSV
                    </button>
                    <button onClick={() => setCreating(true)} className="edu-btn-primary px-4 py-2.5 text-sm inline-flex items-center gap-2">
                        <Plus size={14} /> Crear Usuario
                    </button>
                </div>
            </div>

            {me?.school && (
                <div className="mb-5 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-edu-navy/10 border border-edu-navy/20">
                    <Building2 size={14} className="text-edu-navy" />
                    <span className="text-sm">Estás administrando: <strong className="text-edu-navy">{me.school}</strong></span>
                </div>
            )}

            <div className="relative mb-4">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-edu-muted" />
                <input
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    placeholder="Buscar por nombre o email..."
                    className="edu-input w-full pl-10"
                />
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
                {["", ...ROLES].map((r) => (
                    <button key={r || "todos"} onClick={() => { setFilterRole(r); setPage(1); }}
                        className={`px-4 py-1.5 text-sm rounded-full border font-medium capitalize transition-all ${filterRole === r ? "bg-edu-navy text-white border-edu-navy" : "border-edu text-edu-body hover:bg-edu-soft"}`}>
                        {r === "" ? "Todos" : r}
                    </button>
                ))}
            </div>

            {(filterRole === "alumno" || filterRole === "") && (
                <div className="flex flex-wrap gap-3 mb-5">
                    <select value={filterBaseGrade} onChange={(e) => { setFilterBaseGrade(e.target.value); setFilterSection(""); setPage(1); }} className="edu-input">
                        <option value="">Todos los grados</option>
                        {BASE_GRADES.map((g) => <option key={g}>{g}</option>)}
                    </select>
                    <select value={filterSection} onChange={(e) => { setFilterSection(e.target.value); setPage(1); }} className="edu-input">
                        <option value="">Todas las secciones</option>
                        {(filterBaseGrade ? availableSections : SECTIONS).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
            )}

            <div className="edu-card overflow-hidden">
                <table className="w-full text-sm hidden md:table">
                    <thead className="bg-edu-soft border-b border-edu">
                        <tr className="text-left">
                            <th className="px-4 py-3 font-semibold">Nombre</th>
                            <th className="px-4 py-3 font-semibold">Email</th>
                            <th className="px-4 py-3 font-semibold">Rol</th>
                            <th className="px-4 py-3 font-semibold">Grado / Sección</th>
                            <th className="px-4 py-3 font-semibold">Estado</th>
                            <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginated.length === 0 && (
                            <tr><td colSpan={6} className="text-center py-6 text-edu-muted">Sin usuarios.</td></tr>
                        )}
                        {paginated.map((u) => (
                            <tr key={u.id} className="border-b border-edu/50">
                                <td className="px-4 py-3 font-medium">{u.name}</td>
                                <td className="px-4 py-3 text-edu-body">{u.email}</td>
                                <td className="px-4 py-3 capitalize">{u.role}</td>
                                <td className="px-4 py-3">
                                    {u.role === "profesor" ? (
                                        u.cv_file_id ? (
                                            <a href={fileUrl(u.cv_file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-emerald-100 text-emerald-800">
                                                <Download size={12} /> CV
                                            </a>
                                        ) : <span className="text-xs text-edu-muted">Sin CV</span>
                                    ) : <span className="text-edu-muted">{u.grade || "-"}</span>}
                                </td>
                                <td className="px-4 py-3">
                                    <span className={"edu-pill " + (u.active ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600")}>
                                        {u.active ? "Activo" : "Inactivo"}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => setEditing(u)} className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1 mr-2">
                                        <Edit2 size={12} /> Editar
                                    </button>
                                    <button onClick={() => deactivate(u.id)} className="px-3 py-1 text-xs rounded-lg bg-red-100 text-red-700 inline-flex items-center gap-1">
                                        <Trash2 size={12} /> Desactivar
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="md:hidden divide-y divide-edu/50">
                    {paginated.length === 0 && <div className="text-center py-8 text-edu-muted">Sin usuarios.</div>}
                    {paginated.map((u) => (
                        <div key={u.id} className="p-4 flex flex-col gap-2">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold truncate">{u.name}</div>
                                    <div className="text-xs text-edu-muted truncate">{u.email}</div>
                                    {u.grade && <div className="text-xs text-edu-muted">{u.grade}</div>}
                                </div>
                                <span className={"edu-pill flex-shrink-0 " + (u.active ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600")}>
                                    {u.active ? "Activo" : "Inactivo"}
                                </span>
                            </div>
                            <div className="flex gap-2 pt-1">
                                <button onClick={() => setEditing(u)} className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center justify-center gap-1">
                                    <Edit2 size={12} /> Editar
                                </button>
                                <button onClick={() => deactivate(u.id)} className="flex-1 px-3 py-1.5 text-xs rounded-lg bg-red-100 text-red-700 inline-flex items-center justify-center gap-1">
                                    <Trash2 size={12} /> Desactivar
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-edu">
                        <span className="text-xs text-edu-muted">
                            {filteredUsers.length} usuarios · Página {page} de {totalPages}
                        </span>
                        <div className="flex gap-2">
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                className="p-1.5 rounded-lg border border-edu hover:bg-gray-50 disabled:opacity-40">
                                <ChevronLeft size={16} />
                            </button>
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                className="p-1.5 rounded-lg border border-edu hover:bg-gray-50 disabled:opacity-40">
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {creating && <UserModal title="Crear Usuario" onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
            {editing && <UserModal title="Editar Usuario" user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
            {importing && <ImportModal onClose={() => setImporting(false)} onSaved={() => { setImporting(false); load(); }} school={me ? me.school : ""} />}
        </div>
    );
}

function ImportModal({ onClose, onSaved, school }) {
    const fileRef = useRef();
    const [rows, setRows] = useState([]);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(false);
    const [importRole, setImportRole] = useState("alumno");

    function parseCSV(text) {
        const lines = text.trim().split("\n");
        if (lines.length === 0) return [];
        const splitRow = (row) => row.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || row.split(",");
        const headers = splitRow(lines[0]).map((h) => h.replace(/"/g, "").trim().toLowerCase());
        return lines.slice(1).map((line) => {
            const vals = splitRow(line).map((v) => v.replace(/"/g, "").trim());
            const obj = {};
            headers.forEach((h, i) => { obj[h] = vals[i] || ""; });
            return obj;
        });
    }

    function handleFile(e) {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => { setRows(parseCSV(ev.target.result)); setPreview(true); };
        reader.readAsText(file);
    }

    async function importUsers() {
        setSaving(true);
        let ok = 0, fail = 0;
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            try {
                // Crear alumno
                const res = await api.post("/users", {
                    name: row.nombre || row.name || "",
                    email: row.email || "",
                    password: row.contrasena || row.password || "",
                    role: importRole,
                    grade: importRole === "alumno"
                        ? `${row.grado || row.grade || ""} ${(row.seccion || row.section || "").toUpperCase()}`.trim()
                        : (row.grado || row.grade || ""),
                    school: school,
                    learning_style: row.estilo || row.learning_style || null,
                });
                ok++;

                // Si es alumno y tiene datos de padre, crear padre vinculado
                if (importRole === "alumno") {
                    const emailPadre = row.email_padre || row.parent_email || "";
                    const contrasenaPadre = row.contrasena_padre || row.parent_password || "";
                    const nombrePadre = row.nombre_padre || row.parent_name || "";
                    if (emailPadre && contrasenaPadre) {
                        try {
                            await api.post("/users", {
                                name: nombrePadre || `Padre de ${row.nombre || row.name}`,
                                email: emailPadre,
                                password: contrasenaPadre,
                                role: "padre",
                                school: school,
                                parent_of: res.data.id,
                            });
                        } catch (_) {
                            // Si falla la creación del padre no interrumpimos
                        }
                    }
                }
            } catch(err) { fail++; }
        }
        setSaving(false);
        toast.success(`${ok} usuarios creados ${fail > 0 ? `, ${fail} errores encontrados.` : ""}`);
        onSaved();
    }

    const formatoCSV = importRole === "alumno"
        ? `nombre,email,contrasena,grado,seccion,estilo,email_padre,contrasena_padre,nombre_padre\nJuan Perez,juan@colegio.edu,Pass123,5to Primaria,A,visual,padre@email.com,Pass456,Carlos Perez`
        : `nombre,email,contrasena\nProfesor Juan,juan@colegio.edu,Pass123`;

    const descripcionCSV = importRole === "alumno"
        ? "Columnas: nombre, email, contrasena, grado, seccion, estilo (opcional), email_padre (opcional), contrasena_padre (opcional), nombre_padre (opcional)"
        : "Columnas: nombre, email, contrasena";

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="edu-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">Importar Usuarios desde CSV</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                {!preview ? (
                    <div className="space-y-4">
                        <div>
                            <p className="text-xs font-semibold text-edu-muted uppercase tracking-widest mb-2">Rol a importar</p>
                            <div className="flex flex-wrap gap-2">
                                {ROLES.map((r) => (
                                    <button key={r} onClick={() => setImportRole(r)}
                                        className={`px-3 py-1.5 text-sm rounded-full border font-medium capitalize transition-all ${importRole === r ? "bg-edu-navy text-white border-edu-navy" : "border-edu text-edu-body hover:bg-edu-soft"}`}>
                                        {r}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="bg-edu-soft rounded-xl p-4 text-sm text-edu-body">
                            <p className="font-semibold mb-2">Formato del CSV:</p>
                            <pre className="bg-white rounded-lg p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all">{formatoCSV}</pre>
                            <p className="mt-2 text-xs text-edu-muted">{descripcionCSV}</p>
                            {importRole === "alumno" && (
                                <p className="mt-1 text-xs text-emerald-700 font-medium">✓ Si incluís email_padre y contrasena_padre, el padre se crea automáticamente vinculado al alumno.</p>
                            )}
                        </div>
                        <div className="border-2 border-dashed border-edu rounded-xl p-8 text-center cursor-pointer hover:bg-edu-soft" onClick={() => fileRef.current.click()}>
                            <Upload size={32} className="mx-auto mb-2 text-edu-muted" />
                            <p className="text-sm text-edu-body">Click para seleccionar archivo CSV</p>
                            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <p className="text-sm text-edu-body">Se encontraron <strong>{rows.length} usuarios</strong> para importar
                            {importRole === "alumno" && rows.some(r => r.email_padre || r.parent_email) && (
                                <span className="text-emerald-700"> + padres vinculados</span>
                            )}:
                        </p>
                        <div className="max-h-64 overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-edu-soft">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Nombre</th>
                                        <th className="px-3 py-2 text-left">Email</th>
                                        {importRole === "alumno" && <>
                                            <th className="px-3 py-2 text-left">Grado</th>
                                            <th className="px-3 py-2 text-left">Sección</th>
                                            <th className="px-3 py-2 text-left">Estilo</th>
                                            <th className="px-3 py-2 text-left">Email Padre</th>
                                        </>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={i} className="border-t border-edu/50">
                                            <td className="px-3 py-2">{r.nombre || r.name}</td>
                                            <td className="px-3 py-2">{r.email}</td>
                                            {importRole === "alumno" && <>
                                                <td className="px-3 py-2">{r.grado || r.grade}</td>
                                                <td className="px-3 py-2">{(r.seccion || r.section || "-").toUpperCase()}</td>
                                                <td className="px-3 py-2">{r.estilo || r.learning_style || "-"}</td>
                                                <td className="px-3 py-2">{r.email_padre || r.parent_email || "-"}</td>
                                            </>}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <button onClick={() => setPreview(false)} className="px-4 py-2 text-sm rounded-xl border border-edu">Cambiar archivo</button>
                            <button onClick={importUsers} disabled={saving} className="edu-btn-primary px-4 py-2 text-sm">
                                {saving ? "Importando..." : "Importar " + rows.length + " usuarios"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function UserModal({ title, user, onClose, onSaved }) {
    const isEdit = !!user;
    const [form, setForm] = useState(user || { name: "", email: "", password: "", role: "alumno", grade: "", learning_style: "" });
    const [saving, setSaving] = useState(false);
    const [students, setStudents] = useState([]);

    // Para vincular a un padre con su hijo/a hay que poder elegirlo de la lista de alumnos del colegio.
    useEffect(() => {
        if (form.role !== "padre" || students.length > 0) return;
        api.get("/users", { params: { role: "alumno" } })
            .then((r) => setStudents(r.data || []))
            .catch(() => toast.error("No se pudo cargar la lista de alumnos"));
    }, [form.role]);

    async function save() {
        setSaving(true);
        try {
            if (isEdit) {
                const body = { name: form.name, email: form.email, role: form.role, grade: form.grade, learning_style: form.learning_style };
                if (form.role === "padre" && form.parent_of) body.parent_of = form.parent_of;
                await api.patch("/users/" + user.id, body);
            } else {
                await api.post("/users", { ...form, parent_of: form.role === "padre" ? (form.parent_of || undefined) : undefined });
            }
            toast.success("Usuario guardado con éxito");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Ocurrió un error al guardar los cambios");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="edu-card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">{title}</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-3">
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">Nombre</span>
                        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="edu-input w-full" />
                    </label>
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">Email</span>
                        <input type="text" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="edu-input w-full" />
                    </label>
                    {!isEdit && (
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">Contraseña</span>
                            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="edu-input w-full" />
                        </label>
                    )}
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">Rol</span>
                        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="edu-input w-full">
                            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </label>
                    {form.role === "padre" && (
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">Hijo/a vinculado/a</span>
                            <select value={form.parent_of || ""} onChange={(e) => setForm({ ...form, parent_of: e.target.value })} className="edu-input w-full">
                                <option value="">Sin vincular (no verá información de ningún alumno)</option>
                                {students.map((a) => <option key={a.id} value={a.id}>{a.name}{a.grade ? ` — ${a.grade}` : ""}</option>)}
                            </select>
                            <span className="text-xs text-edu-muted mt-1 block">El padre solo podrá ver el perfil, las notas y las monedas del alumno que elijas aquí.</span>
                        </label>
                    )}
                    {form.role === "alumno" && (
                        <>
                            <label className="block">
                                <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">Grado y Sección</span>
                                <select value={form.grade || ""} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="edu-input w-full">
                                    <option value="">-</option>
                                    {GRADE_SECTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">Estilo de aprendizaje</span>
                                <select value={form.learning_style || ""} onChange={(e) => setForm({ ...form, learning_style: e.target.value })} className="edu-input w-full">
                                    <option value="">Sin asignar</option>
                                    {LEARNING_STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </label>
                        </>
                    )}
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                        <button onClick={save} disabled={saving} className="edu-btn-primary px-4 py-2 text-sm">
                            {saving ? "Guardando..." : "Guardar"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
